import type { DrawdownPath } from '@/types/api'
import {
  transformDrawdownData,
  formatCurrency,
  formatDrawdownDate,
  CHART_COLORS,
  type DrawdownDataPoint,
} from '@/lib/chart-transformers'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceDot,
  Area,
} from 'recharts'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { TrendingDown, Activity, Calendar, Info } from 'lucide-react'

interface DrawdownChartProps {
  data: DrawdownPath | null
  height?: number
}

interface CustomTooltipProps {
  active?: boolean
  payload?: Array<{
    payload: DrawdownDataPoint
  }>
  label?: string
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (active && payload && payload.length > 0) {
    const data = payload[0].payload

    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
        <p className="font-semibold text-gray-900">{formatDrawdownDate(label || '')}</p>
        <p className="text-sm text-gray-600">
          NAV: {formatCurrency(data.nav)}
        </p>
        <p className={`text-sm ${data.drawdown < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
          Drawdown: {data.drawdown.toFixed(2)}%
        </p>
        {data.isPeak && <Badge className="mt-1 bg-emerald-100 text-emerald-800">Peak</Badge>}
        {data.isTrough && <Badge className="mt-1 bg-red-100 text-red-800">Trough</Badge>}
        {data.isRecovery && <Badge className="mt-1 bg-blue-100 text-blue-800">Recovery</Badge>}
      </div>
    )
  }
  return null
}

export function DrawdownChart({ data, height = 350 }: DrawdownChartProps) {
  if (!data || !data.points || data.points.length === 0) {
    return <EmptyDrawdownChart height={height} />
  }

  const chartData = transformDrawdownData(data)
  const maxDrawdown = Number(data.max_drawdown)
  const maxDrawdownDate = data.max_drawdown_date

  // Find peak, trough, and recovery points
  const peakPoint = chartData.find(d => d.isPeak)
  const troughPoint = chartData.find(d => d.isTrough)
  const recoveryPoint = chartData.find(d => d.isRecovery)

  // Calculate duration
  const startDate = new Date(chartData[0].date)
  const endDate = new Date(chartData[chartData.length - 1].date)
  const durationDays = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              Drawdown Path
              <TrendingDown className="h-5 w-5 text-red-500" />
            </CardTitle>
            <CardDescription>
              Cumulative NAV over time with peak, trough, and recovery markers
            </CardDescription>
          </div>
          <Badge variant="outline" className="text-xs">
            Historical Replay
          </Badge>
        </div>
      </CardHeader>

      <CardContent>
        <div style={{ height }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
            >
              <defs>
                <linearGradient id="navGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CHART_COLORS.positive} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={CHART_COLORS.positive} stopOpacity={0} />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />

              <XAxis
                dataKey="date"
                tickFormatter={formatDrawdownDate}
                tick={{ fontSize: 11 }}
              />

              <YAxis
                tickFormatter={(value) => formatCurrency(Number(value))}
                tick={{ fontSize: 11 }}
                domain={['auto', 'auto']}
              />

              <Tooltip content={<CustomTooltip />} />

              {/* Reference line at y=0 for drawdown */}
              <ReferenceLine
                y={chartData[0]?.nav || 0}
                stroke="#6b7280"
                strokeDasharray="5 5"
                label={{ value: 'Start NAV', position: 'insideTopRight', fontSize: 11 }}
              />

              {/* Peak point */}
              {peakPoint && (
                <ReferenceDot
                  x={peakPoint.date}
                  y={peakPoint.nav}
                  r={6}
                  fill={CHART_COLORS.positive}
                  stroke="#fff"
                  strokeWidth={2}
                />
              )}

              {/* Trough point */}
              {troughPoint && (
                <ReferenceDot
                  x={troughPoint.date}
                  y={troughPoint.nav}
                  r={6}
                  fill={CHART_COLORS.negative}
                  stroke="#fff"
                  strokeWidth={2}
                />
              )}

              {/* Recovery point */}
              {recoveryPoint && (
                <ReferenceDot
                  x={recoveryPoint.date}
                  y={recoveryPoint.nav}
                  r={6}
                  fill={CHART_COLORS.total}
                  stroke="#fff"
                  strokeWidth={2}
                />
              )}

              {/* Area under the line */}
              <Area
                type="monotone"
                dataKey="nav"
                stroke="none"
                fill="url(#navGradient)"
                isAnimationActive={true}
                animationDuration={1000}
              />

              {/* Main line */}
              <Line
                type="monotone"
                dataKey="nav"
                stroke={CHART_COLORS.total}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }}
                isAnimationActive={true}
                animationDuration={1000}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>

      <CardFooter className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-red-500" />
          <Badge variant="outline" className="font-mono text-red-600">
            {maxDrawdown.toFixed(2)}%
          </Badge>
          <span className="text-gray-600">Max Drawdown</span>
        </div>

        {troughPoint && (
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <span className="text-gray-600">
              Trough: {formatDrawdownDate(maxDrawdownDate)}
            </span>
          </div>
        )}

        {recoveryPoint && (
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="font-mono text-emerald-600">
              Recovered
            </Badge>
            <span className="text-gray-600">
              on {formatDrawdownDate(recoveryPoint.date)}
            </span>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Info className="h-4 w-4 text-gray-500" />
          <span className="text-gray-500">
            Duration: {durationDays} days
          </span>
        </div>
      </CardFooter>
    </Card>
  )
}

interface EmptyDrawdownChartProps {
  height?: number
  reason?: 'no-data' | 'not-available'
}

export function EmptyDrawdownChart({
  height = 350,
  reason = 'not-available',
}: EmptyDrawdownChartProps) {
  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              Drawdown Path
              <TrendingDown className="h-5 w-5 text-gray-400" />
            </CardTitle>
            <CardDescription>
              Cumulative NAV over time with peak, trough, and recovery markers
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div
          style={{ height }}
          className="flex flex-col items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-200"
        >
          <TrendingDown className="h-12 w-12 text-gray-300 mb-3" />
          <p className="text-gray-500 text-center font-medium">
            {reason === 'no-data'
              ? 'No drawdown data available'
              : 'Drawdown path not available for this scenario mode'}
          </p>
          <p className="text-gray-400 text-sm text-center mt-1 max-w-md">
            {reason === 'no-data'
              ? 'The scenario did not generate drawdown path data.'
              : 'Drawdown paths are only generated for Historical Replay mode. BTC Curve + Regime and Custom Shocks modes do not include drawdown visualization.'}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
